import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { API_BASE_URL, GET_ORDERS_BY_ID } from "../../Config";
import SpinLoader from "../loader/SpinLoader";

const SaleView = ({ saleObj }) => {
  const [userDataSession, setUserDataSession] = useState([]);
  const [ordersDetailsData, setOrdersDetailsData] = useState([]);
  const [OrderPayments, setOrderPayments] = useState([]); // ← added back state
  const [detailsLoading, setDetailsLoading] = useState(false);
  const saleRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const userDataString = sessionStorage.getItem("userData");
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUserDataSession(userData);
    } else {
      console.log("User data not found in sessionStorage");
    }
  }, []);

  useEffect(() => {
    if (userDataSession?.OrgId && saleObj?.Id) {
      setDetailsLoading(true);

      // Fetch order details
      Axios.get(`${API_BASE_URL}${GET_ORDERS_BY_ID}`, {
        params: { OrgId: userDataSession.OrgId, OrderId: saleObj.Id },
      })
        .then((response) => {
          if (response.data.Status) {
            setOrdersDetailsData(response.data.ResultData);
          } else {
            setOrdersDetailsData([]);
          }
        })
        .catch((error) => console.error("Error fetching order data:", error));

      // Fetch payments
      Axios.get(`${API_BASE_URL}Billing/GetOrderPaymentsById`, {
        params: { OrgId: userDataSession.OrgId, OrderId: saleObj.Id },
      })
        .then((response) => {
          if (response.data.Status) {
            setOrderPayments(response.data.ResultData || []);
          }
        })
        .catch((error) => console.error("Error fetching payments:", error))
        .finally(() => setDetailsLoading(false));
    }
  }, [userDataSession?.OrgId, saleObj?.Id]);

  const handleDownloadPDF = () => {
    setPdfLoading(true);
    if (!saleRef.current) return;

    const node = saleRef.current;
    const images = node.querySelectorAll("img");
    Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => (img.onload = resolve));
      }),
    )
      .then(() => {
        html2canvas(node, { scale: 1.5 }).then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF("p", "mm", "a4");
          const imgWidth = 210;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
          pdf.save(`Sale_Order_${saleObj?.OrderNo || "N/A"}.pdf`);
        });
      })
      .finally(() => setPdfLoading(false));
  };

  const handlePrint = () => {
    if (!saleRef.current) return;
    setPdfLoading(true);

    const printWindow = window.open("", "", "width=900,height=700");
    if (!printWindow) return;

    const saleContent = saleRef.current.innerHTML;

    printWindow.document.write(`
      <html>
      <head>
        <title>Print Sale Order</title>
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css">
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .table { width: 100%; border-collapse: collapse; }
          .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          .table th { background-color: #f2f2f2; }
        </style>
      </head>
      <body>
        ${saleContent}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(() => window.close(), 100);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    setPdfLoading(false);
  };

  return (
    <div>
      {pdfLoading && <SpinLoader />}

      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvasRightSaleView"
        aria-labelledby="offcanvasRightLabel"
        style={{ minWidth: "45%", maxWidth: "45%" }}
      >
        <div className="offcanvas-header d-flex justify-content-between align-items-center">
          <h4 id="offcanvasRightLabel" className="mb-0">
            View Sale - {saleObj?.OrderNo || "N/A"}
          </h4>
          <div className="d-flex gap-2">
            <button onClick={handleDownloadPDF} className="btn btn-danger">
              <i className="fa-regular fa-file-pdf fa-lg"></i>
            </button>
            <button className="btn btn-primary" onClick={handlePrint}>
              <i className="fa-solid fa-print fa-lg"></i>
            </button>
            <button
              type="button"
              className="btn-close text-reset"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            ></button>
          </div>
        </div>
        <hr className="m-0 p-0" />

        <div className="offcanvas-body p-3" ref={saleRef}>
          {/* Header info */}
          <div className="row g-3 mb-4 ">
            <div className="col-md-6">
              <strong>Store:</strong>{" "}
              <span className="text-primary">
                {saleObj?.StoreName || "N/A"}
              </span>
            </div>
            <div className="col-md-6">
              <strong>Date:</strong>{" "}
              <span className="text-primary">
                {saleObj?.OrderDate || "N/A"}
              </span>
            </div>
            <div className="col-md-4">
              <strong>Employee:</strong>{" "}
              <span className="text-primary">
                {ordersDetailsData[0]?.USerName || "N/A"}
              </span>
            </div>
            <div className="col-md-4">
              <strong>Customer:</strong>{" "}
              <span className="text-primary">
                {saleObj?.CustomerName || "N/A"}
              </span>
            </div>
            <div className="col-md-4">
              <strong>Discount:</strong>{" "}
              <span className="text-primary">
                Rs.{Number(saleObj?.Discount || 0).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Main card with products + payments at bottom */}
          <div className="card">
            <div className="card-header bg-light">
              <h5 className="mb-0"> Order Items</h5>
            </div>
            <div className="card-body p-0">
              <div
                style={{
                  maxHeight: "170px",
                  overflowY: "auto",
                }}
              >
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead className="sticky-top bg-white ">
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>Quantity</th>
                        {/* <th>Discount</th> */}
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsLoading ? (
                        <tr>
                          <td colSpan={5} className="text-center py-4">
                            <SpinLoader />
                          </td>
                        </tr>
                      ) : ordersDetailsData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center py-4 text-muted"
                          >
                            No items found
                          </td>
                        </tr>
                      ) : (
                        ordersDetailsData.map((item, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{item.ProductName}</td>
                            <td>{item.Quantity}</td>
                            {/* <td>{item.Discount}</td> */}
                            <td>{item.UnitPrice}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payments section - at the bottom of the table area */}
            </div>
          </div>

          <div className="card">
            <div className="card-header bg-light">
              <h5 className="mb-0">Payment Details</h5>
            </div>
            <div className="card-body">
              {OrderPayments.length > 0 ? (
                <>
                  <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
                    {/* Individual Payments - left side */}
                    <div className="d-flex flex-wrap gap-3">
                      {OrderPayments.map((payment, index) => (
                        <div
                          key={index}
                          className="px-3 py-1 bg-light border rounded-pill d-flex align-items-center gap-2"
                        >
                          <span className="text-muted small">
                            {payment.PaymentType}
                          </span>
                          <span className="fw-bold text-success">
                            Rs.{Number(payment.PaidAmount).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Total - right aligned with primary color */}
                    <div className="ms-auto px-4 py-2 bg-primary border-0 rounded-pill d-flex align-items-center gap-2 text-white ">
                      <span className="fw-semibold">Total</span>
                      <span className="fw-bold">
                        Rs.
                        {OrderPayments.reduce(
                          (sum, p) => sum + Number(p.PaidAmount || 0),
                          0,
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-3 small">
                  No payment details available
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer-btn p-3 border-top">
          <button
            type="button"
            className="btn btn-cancel me-2"
            data-bs-dismiss="offcanvas"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

SaleView.propTypes = {
  saleObj: PropTypes.object.isRequired,
};

export default SaleView;
